import { getAuthApiUrl, getStoredSessionToken } from '../../../app/services/session.service';

function buildResult({ ok, data = null, error = null, message = '' }) {
  return {
    ok,
    data,
    error,
    message,
  };
}

function getAdminApiUrl(path) {
  return getAuthApiUrl(path);
}

function getAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();

  if (!token) {
    return extraHeaders;
  }

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

function mapApiTenantUser(user) {
  return {
    id: user?.id ?? null,
    fullName: user?.fullName || '',
    email: user?.email || '',
    role: user?.role || '',
    active: user?.active !== false,
    tenantId: user?.tenantId || '',
  };
}

export async function listTenantUsers(tenantId) {
  try {
    const response = await fetch(getAdminApiUrl(`/api/admin/tenants/${tenantId}/users`), {
      headers: getAuthHeaders(),
    });

    if (response.status === 403) {
      return buildResult({
        ok: false,
        error: 'ADMIN_FORBIDDEN',
        message: 'Accesso admin non consentito per la sessione corrente.',
      });
    }

    if (response.status === 404) {
      return buildResult({
        ok: false,
        error: 'TENANT_NOT_FOUND',
        message: 'Tenant non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`ADMIN_TENANT_USERS_LIST_FAILED_${response.status}`);
    }

    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new Error('ADMIN_TENANT_USERS_INVALID_PAYLOAD');
    }

    return buildResult({
      ok: true,
      data: {
        users: payload.map(mapApiTenantUser),
      },
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'ADMIN_TENANT_USERS_UNAVAILABLE',
      message: 'Lista utenti tenant non disponibile.',
    });
  }
}

export async function createTenantUser(tenantId, payload) {
  try {
    const response = await fetch(getAdminApiUrl(`/api/admin/tenants/${tenantId}/users`), {
      method: 'POST',
      headers: getAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    });

    if (response.status === 403) {
      return buildResult({
        ok: false,
        error: 'ADMIN_FORBIDDEN',
        message: 'Accesso admin non consentito per la sessione corrente.',
      });
    }

    if (response.status === 400) {
      return buildResult({
        ok: false,
        error: 'TENANT_USER_VALIDATION_ERROR',
        message: 'Dati utente non validi.',
      });
    }

    if (response.status === 404) {
      return buildResult({
        ok: false,
        error: 'TENANT_NOT_FOUND',
        message: 'Tenant non trovato.',
      });
    }

    if (response.status === 409) {
      return buildResult({
        ok: false,
        error: 'TENANT_USER_EMAIL_CONFLICT',
        message: 'Email gia in uso.',
      });
    }

    if (!response.ok) {
      throw new Error(`ADMIN_TENANT_USER_CREATE_FAILED_${response.status}`);
    }

    const createdUser = await response.json();

    return buildResult({
      ok: true,
      data: {
        user: mapApiTenantUser(createdUser),
      },
      message: 'Utente tenant creato.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'TENANT_USER_CREATE_UNAVAILABLE',
      message: 'Creazione utente non completata.',
    });
  }
}

export async function updateTenantUserStatus(tenantId, userId, active) {
  try {
    const response = await fetch(
      getAdminApiUrl(`/api/admin/tenants/${tenantId}/users/${userId}/status`),
      {
        method: 'PATCH',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ active }),
      }
    );

    if (response.status === 403) {
      return buildResult({
        ok: false,
        error: 'ADMIN_FORBIDDEN',
        message: 'Accesso admin non consentito per la sessione corrente.',
      });
    }

    if (response.status === 400) {
      return buildResult({
        ok: false,
        error: 'TENANT_USER_STATUS_INVALID',
        message: 'Aggiornamento utente non consentito.',
      });
    }

    if (response.status === 404) {
      return buildResult({
        ok: false,
        error: 'TENANT_USER_NOT_FOUND',
        message: 'Utente tenant non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`ADMIN_TENANT_USER_STATUS_FAILED_${response.status}`);
    }

    const payload = await response.json();

    return buildResult({
      ok: true,
      data: {
        user: mapApiTenantUser(payload),
      },
      message: active ? 'Utente attivato.' : 'Utente disattivato.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'TENANT_USER_STATUS_UNAVAILABLE',
      message: 'Aggiornamento stato utente non completato.',
    });
  }
}
